import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { NavPayloadAction } from '@carvana/merch-native';
import DispatcherEvent from '@carvana/merch-native/DispatcherEvent';
import {
  SearchBar as MerchSearchBar,
  SearchBarProps
} from '@carvana/merch-search-bar';
import dynamic from 'next/dynamic';
import { useContext, useMemo, useState } from 'react';
import { apikUrl, assetsCdnUrl } from '~/constants/environment';
import { useIsMobile, useIsNative } from '~/context/common/DeviceContext';
import { useConsumer } from '~/context/common/StaticContext';
import UserContext from '~/context/common/UserContext';
import ModalContext from '~/context/home/ModalContext';
import { useSavedSearchesForSearchBar } from '~/context/home/SavedSearchesContext';
import sendMessageToNative from '~/native/home/dispatchers/sendMessageToNative';
import { generateErrorLogger } from '~/utilities/loggers';

const SearchBarModal = dynamic(
  () =>
    import(
      /* webpackChunkName: "SearchBarModal" */ '@/home/SearchBar/SearchBarModal'
    ),
  { ssr: false }
);

const onSuggestionsApiError = generateErrorLogger('Get Suggestions', {});

export default function SearchBar() {
  const { trackEvent } = useContext(AnalyticsContext);
  const consumer = useConsumer();
  const savedSearches = useSavedSearchesForSearchBar();
  const { setSearchBarModalOpen, setIsSearchBarFocused } =
    useContext(ModalContext);
  const [inputValue, setInputValue] = useState('');
  const {
    location: {
      data: { state = '' }
    }
  } = useContext(UserContext);
  const isMobile = useIsMobile();
  const isNative = useIsNative();

  const searchBarProps: SearchBarProps = useMemo(
    () => ({
      apiBaseUrl: apikUrl,
      buttonSize: 'large',
      cdnBaseUrl: assetsCdnUrl,
      geolocationState: state,
      inputValue,
      onCloseModal: () => {
        setSearchBarModalOpen(false);
      },
      onFocus: setIsSearchBarFocused,
      onShowModalClick: () => {
        setSearchBarModalOpen(true);
      },
      onSuggestApiError: onSuggestionsApiError,
      onSuggestionSelection: isNative
        ? ({ cvnaId, url }) => {
            sendMessageToNative(DispatcherEvent.LeaveFullscreen, {});

            sendMessageToNative(DispatcherEvent.Link, {
              url: `${process.env.CVNA_APP_ABSOLUTE_BASE_URL}${url}`,
              linkType: 'browser',
              action: NavPayloadAction.ViewSrp,
              cvnaid: cvnaId
            });
          }
        : undefined,
      refSource: consumer,
      rounded: 'md',
      savedSearches: savedSearches,
      setInputValue,
      showModal: isMobile,
      trackEvent: trackEvent
    }),
    [
      consumer,
      inputValue,
      isMobile,
      isNative,
      savedSearches,
      setIsSearchBarFocused,
      setSearchBarModalOpen,
      state,
      trackEvent
    ]
  );

  return (
    <>
      <MerchSearchBar {...searchBarProps} />
      {isMobile && <SearchBarModal {...searchBarProps} />}
    </>
  );
}
